import PropTypes from 'prop-types';
import { WarningIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PortalIdParser from 'PortalIdParser';
import { DOMAIN_VERIFICATION_STATUS } from '../data/models/ApplicationAuthorization';
import { useSelector } from 'react-redux';
import { getIsRequestingNewScopes } from '../selectors/AppSelectors';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const portalId = PortalIdParser.get();
const AuthorizeAlert = ({
  domainStatus,
  cannotInstall,
  account
}) => {
  const hasVerifiedDomain = domainStatus === DOMAIN_VERIFICATION_STATUS.VERIFIED_DOMAIN;
  const isRequestingNewScopes = useSelector(getIsRequestingNewScopes);
  const accountName = account.accountName;
  const translationKey = hasVerifiedDomain ? 'verified' : 'notVerified';
  const previouslyConnectedButNoInstall = !isRequestingNewScopes && cannotInstall;

  // We have three cases here, sorted in order of priority in terms of the alert overrides:
  // 1.) Has the app been previously installed but the user does not have app marketplace access
  // 2.) If the app hasn't been previously installed, does the user not have app marketplace access?
  // 3.) If the app has a verified domain or not
  const renderAlertTitle = () => {
    if (previouslyConnectedButNoInstall) {
      if (accountName) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.app.marketplaceAccess.alreadyConnectedTitle",
          options: {
            accountName
          }
        });
      }
      // If no account name has been specified
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.alreadyConnectedTitlePortal",
        options: {
          portalId
        }
      });
    }
    if (cannotInstall) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionTitle"
      });
    }
    if (hasVerifiedDomain) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.notListed.verifiedTitle"
      });
    }
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(View, {
        as: "span",
        paddingInlineEnd: "200",
        children: /*#__PURE__*/_jsx(WarningIcon, {
          size: "600",
          color: "icon-alert-default"
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.notListed.notVerifiedTitle"
      })]
    });
  };
  const renderAlertBody = () => {
    if (previouslyConnectedButNoInstall) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.alreadyConnectedBody"
      });
    }
    if (cannotInstall) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionBody_jsx",
        elements: {
          Link
        },
        options: {
          href: 'https://knowledge.hubspot.com/settings/hubspot-user-permissions-guide#settings'
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `oauth.app.notListed.${translationKey}Body`
    });
  };
  const getAlertType = () => {
    if (hasVerifiedDomain && !cannotInstall) {
      return 'warning';
    } else if (previouslyConnectedButNoInstall) {
      return 'info';
    }
    return 'danger';
  };
  return /*#__PURE__*/_jsx(Alert, {
    titleText: renderAlertTitle(),
    type: getAlertType(),
    marginBlockStart: "400",
    children: renderAlertBody()
  });
};
AuthorizeAlert.propTypes = {
  domainStatus: PropTypes.string.isRequired,
  cannotInstall: PropTypes.bool.isRequired
};
export default AuthorizeAlert;